import { computed } from 'vue';
import { useUserStore } from '@/store/user';

/**
 * Canonical keys that an app customization may list in `hidden_features`.
 * These are the only keys the app understands; any other (future/unknown)
 * key in the array is ignored gracefully.
 */
export const FEATURE_KEYS = {
  report: 'report',
  groups: 'groups',
  checklists: 'checklists',
  warnings: 'warnings',
  warningList: 'warning_list',
  securityCenter: 'security_center',
  police: 'police',
} as const;

export type FeatureKey = typeof FEATURE_KEYS[keyof typeof FEATURE_KEYS];

export type SectionVisibility = {
  groupsHidden: boolean;
  checklistsHidden: boolean;
  warningsHidden: boolean;
  notificationSettingsHidden?: boolean;
};

/**
 * Pure helper for the router guard: given a target path and the current section
 * visibility, returns the redirect target ('/dashboard') if the path belongs to
 * a hidden section, or `null` to allow navigation. The prefix match also covers
 * each section's sub-routes (e.g. `/groups/show/:id`).
 */
export function hiddenSectionRedirect(
  path: string,
  visibility: SectionVisibility
): string | null {
  const sections = [
    { matches: path.startsWith('/groups'), hidden: visibility.groupsHidden },
    { matches: path.startsWith('/checklists'), hidden: visibility.checklistsHidden },
    { matches: path.startsWith('/warnings'), hidden: visibility.warningsHidden },
    {
      matches: path.startsWith('/dashboard/notifications'),
      hidden: !!visibility.notificationSettingsHidden,
    },
  ];

  return sections.some((section) => section.matches && section.hidden) ? '/dashboard' : null;
}

/**
 * Single source of truth for which parts of the app the current app
 * customization wants hidden. Reads `hidden_features` reactively from the
 * user store, so the UI updates once `fetchUser()` resolves. Before the user
 * is loaded (or when the field is missing/null) nothing is hidden.
 *
 * Parent/child rule: a warning source is hidden if its own key OR the parent
 * `warnings` key is present. The `warnings` section as a whole is hidden when
 * `warnings` is listed, or when every individual warning source is hidden.
 */
export function useFeatureVisibility() {
  const userStore = useUserStore();

  const hiddenFeatures = computed<string[]>(
    () => userStore.currentUser?.app_customization?.hidden_features ?? []
  );

  const isHidden = (key: FeatureKey): boolean => hiddenFeatures.value.includes(key);

  const reportHidden = computed(() => isHidden(FEATURE_KEYS.report));
  const groupsHidden = computed(() => isHidden(FEATURE_KEYS.groups));
  const checklistsHidden = computed(() => isHidden(FEATURE_KEYS.checklists));

  // Warning sources fold in the parent `warnings` key.
  const warningListHidden = computed(
    () => isHidden(FEATURE_KEYS.warningList) || isHidden(FEATURE_KEYS.warnings)
  );
  const securityCenterHidden = computed(
    () => isHidden(FEATURE_KEYS.securityCenter) || isHidden(FEATURE_KEYS.warnings)
  );
  const policeHidden = computed(
    () => isHidden(FEATURE_KEYS.police) || isHidden(FEATURE_KEYS.warnings)
  );

  // The whole Varningar section is hidden when explicitly listed, or when
  // every warning source ends up hidden (so we never show an empty tab).
  const warningsHidden = computed(
    () => warningListHidden.value && securityCenterHidden.value && policeHidden.value
  );

  // Every notification setting belongs to one of these three areas, so the
  // settings page (and the link to it) has nothing left to show once all of
  // them are hidden.
  const notificationSettingsHidden = computed(
    () => groupsHidden.value && checklistsHidden.value && warningsHidden.value
  );

  return {
    hiddenFeatures,
    isHidden,
    reportHidden,
    groupsHidden,
    checklistsHidden,
    warningsHidden,
    warningListHidden,
    securityCenterHidden,
    policeHidden,
    notificationSettingsHidden,
  };
}

export default useFeatureVisibility;
